/**
 * Removes leading zeros from a number string while preserving decimal values
 * @param value - The input string to format
 * @returns The formatted string without leading zeros
 */
const removeLeadingZeros = (value: string): string => {
  if (!value) return value
  // Keep single zero
  if (value === "0" || value === ".") return value
  // Keep decimal numbers starting with 0 (e.g., 0.5)
  if (value.startsWith("0.")) return value
  // Keep numbers ending with decimal point
  if (value.endsWith(".")) {
    const withoutLeadingZeros = value.replace(/^0+/, "")
    return withoutLeadingZeros || "0."
  }
  // Remove leading zeros
  return value.replace(/^0+(\d)/, "$1")
}

/**
 * Formats a number with commas as thousands separators.
 *
 * @param {number|string} num - The number to format
 * @param {number} maximumFractionDigits - Maximum number of decimal places (default: 5)
 * @param {number} minimumFractionDigits - Minimum number of decimal places (default: 2)
 * @returns {string} The formatted number as a string
 */
export const formatNumber = (
  num: string | number | null,
  maximumFractionDigits = 5,
  minimumFractionDigits = 2
): string => {
  if (num === null || num === undefined) return ""
  return Number(num).toLocaleString("en-US", { minimumFractionDigits, maximumFractionDigits })
}

/**
 * Handles numeric input validation and formatting
 */

/**
 * Formats a numeric value, handling empty strings and negative numbers
 * @param value - The input value to format
 * @returns The formatted value as string or number
 */
export const formatNumericInput = (value: string): string | number => {
  if (!value) return ""
  // Keep the decimal point during typing
  if (value === "." || value.endsWith(".")) return value
  const formattedValue = removeLeadingZeros(value)
  const numericValue = parseFloat(formattedValue)
  if (Number.isNaN(numericValue)) return ""
  return numericValue < 0 ? Math.abs(numericValue) : numericValue
}

export function formatNumberString(value: string, precision = 2): string {
  if (!value) return ""
  if (value === "." || value.endsWith(".")) return value

  if (value.startsWith("0") && !value.startsWith("0.")) {
    value = value.replace(/^0+/, "")
    if (value === "") value = "0"
  }

  const num = Number(value)
  if (!isNaN(num)) {
    const [intPart, decPart] = value.split(".")
    if (decPart && decPart.length > precision) {
      return `${intPart}.${decPart.slice(0, precision)}`
    }
    return value
  }

  return ""
}

export function roundToPrecision(num: number, precision: number): number {
  const factor = Math.pow(10, precision)
  const epsilon = 0.00000000000001

  return Math.round(num * factor + epsilon) / factor
}
